/**
 * The register's export, in the browser's own words — and the browser's own writer for the demo.
 *
 * TWO MODULES, ONE CONTRACT
 * -------------------------
 * The product's export is composed by the SERVER: it is the whole filtered set, which is thousands
 * of rows the browser never holds, and it is the server that knows how to page through them. This
 * module exists for two narrower reasons:
 *
 *   - the words. The file's status column and the register's status pill have to say the same
 *     thing, and the way to guarantee that is one written-down vocabulary that both packages are
 *     tested against — `tests/fixtures/credential-export-columns.json`. Neither side imports the
 *     other; both are checked against the file, so a drift in either direction turns a test red.
 *   - the demo. `dist-demo` has no server at all. Its «Dışa aktar» composes here, from the records
 *     the tab is already holding, so the control is not a dead button on the surface the Product
 *     Owner reviews.
 *
 * The formula guard and the byte order mark are the same two rules as the server's writer, for the
 * same two reasons: a cell beginning `=` is code to a spreadsheet, and a BOM-less UTF-8 file opens
 * as the machine's code page on a Turkish Windows desktop.
 */

import type { ProfileAddStatus, ShareStatus } from './engagement-labels';

export const CREDENTIAL_EXPORT_COLUMNS = [
  { key: 'publicCode', header: 'Sertifika kodu', optional: false },
  { key: 'holder', header: 'Alıcı', optional: false },
  { key: 'subjectType', header: 'Kayıt türü', optional: false },
  { key: 'template', header: 'Şablon', optional: false },
  { key: 'templateRelease', header: 'Şablon sürümü', optional: false },
  { key: 'issueDate', header: 'Veriliş tarihi', optional: false },
  { key: 'expiresAt', header: 'Geçerlilik bitişi', optional: false },
  { key: 'status', header: 'Durum', optional: false },
  { key: 'delivery', header: 'E-posta teslimi', optional: false },
  { key: 'linkedinProfile', header: 'LinkedIn Profil', optional: false },
  { key: 'linkedinShare', header: 'LinkedIn Paylaşım', optional: false },
  { key: 'verificationUrl', header: 'Doğrulama adresi', optional: false },
  { key: 'updatedAt', header: 'Son güncellenme', optional: false },
  { key: 'recipientEmail', header: 'Alıcı e-postası', optional: true },
] as const;

export type ExportColumnKey = (typeof CREDENTIAL_EXPORT_COLUMNS)[number]['key'];

export const EXPORT_SUBJECT_TYPE_TR: Record<'person' | 'organization', string> = {
  person: 'Kişi',
  organization: 'Kurum',
};

export const EXPORT_STATUS_TR: Record<string, string> = {
  pending_acceptance: 'Onay bekliyor',
  pending_admin_publish: 'Yayın onayı bekliyor',
  active: 'Aktif',
  expired: 'Süresi dolmuş',
  declined: 'Reddedildi',
  revoked: 'İptal edilmiş',
  superseded: 'Yerine yenisi düzenlendi',
};

export const EXPORT_DELIVERY_TR: Record<string, string> = {
  none: 'Gönderilmedi',
  disabled: 'Gönderilmedi',
  queued: 'Kuyrukta',
  created: 'Kuyrukta',
  superseded: 'Kuyrukta',
  sent: 'Gönderildi',
  delivered: 'Teslim edildi',
  bounced: 'Geri döndü',
  failed: 'Başarısız',
};

export const EXPORT_PROFILE_ADD_TR: Record<ProfileAddStatus, string> = {
  started: 'Ekleme başlatıldı',
  none: 'İşlem kaydı yok',
  unknown: 'Bilinmiyor',
  not_applicable: 'Uygulanmaz',
};

export const EXPORT_SHARE_TR: Record<ShareStatus, string> = {
  published: 'Paylaşıldı',
  result_unknown: 'Sonuç bilinmiyor',
  started: 'Paylaşım başlatıldı',
  none: 'İşlem kaydı yok',
  unknown: 'Bilinmiyor',
};

export function exportDeliveryLabel(status: string | null): string {
  if (status === null || status === 'none') return EXPORT_DELIVERY_TR.disabled as string;
  return EXPORT_DELIVERY_TR[status] ?? (EXPORT_DELIVERY_TR.queued as string);
}

export interface ExportFacts {
  publicCode: string;
  holder: string;
  subjectType: 'person' | 'organization';
  templateName: string;
  releaseNumber: number;
  issueDate: string;
  expiresAt: string | null;
  status: string;
  deliveryStatus: string | null;
  profileAdd: ProfileAddStatus;
  share: ShareStatus;
  verificationUrl: string;
  updatedAt: string;
  recipientEmail: string | null;
}

export function exportShape(includeRecipientEmail: boolean): {
  headers: string[];
  keys: ExportColumnKey[];
} {
  const cols = CREDENTIAL_EXPORT_COLUMNS.filter((c) => !c.optional || includeRecipientEmail);
  return { headers: cols.map((c) => c.header), keys: cols.map((c) => c.key) };
}

export function exportRow(facts: ExportFacts, keys: readonly ExportColumnKey[]): string[] {
  const by: Record<ExportColumnKey, string> = {
    publicCode: facts.publicCode,
    holder: facts.holder,
    subjectType: EXPORT_SUBJECT_TYPE_TR[facts.subjectType],
    template: facts.templateName,
    templateRelease: `v${facts.releaseNumber}`,
    issueDate: facts.issueDate,
    expiresAt: facts.expiresAt ?? 'Süresiz',
    status: EXPORT_STATUS_TR[facts.status] ?? facts.status,
    delivery: exportDeliveryLabel(facts.deliveryStatus),
    linkedinProfile: EXPORT_PROFILE_ADD_TR[facts.profileAdd],
    linkedinShare: EXPORT_SHARE_TR[facts.share],
    verificationUrl: facts.verificationUrl,
    updatedAt: facts.updatedAt,
    recipientEmail: facts.recipientEmail ?? '',
  };
  return keys.map((k) => by[k]);
}

export const CSV_BOM = '﻿';
const NEEDS_QUOTE = /[",\r\n]/;
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/** A cell a spreadsheet reads as text, whatever a person typed into the record. */
export function csvCell(value: string): string {
  const inert = FORMULA_LEAD.test(value) ? `'${value}` : value;
  return NEEDS_QUOTE.test(inert) ? `"${inert.replace(/"/g, '""')}"` : inert;
}

export function csvFile(
  headers: readonly string[],
  rows: readonly (readonly string[])[],
  opts: { preface?: string } = {},
): string {
  const row = (cells: readonly string[]): string => cells.map(csvCell).join(',');
  const lines = [...(opts.preface ? [row([opts.preface])] : []), row(headers), ...rows.map(row)];
  return `${CSV_BOM}${lines.join('\r\n')}\r\n`;
}

/** ASCII, because the Turkish belongs in the file rather than in a header a browser re-encodes. */
export function csvFilename(prefix: string, stamp: Date): string {
  return `${prefix}-${stamp.toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
}

/**
 * Hands the bytes to the browser as a download, and cleans up after itself.
 *
 * A `blob:` URL is held by the browser until it is released; a screen whose export can be pressed
 * repeatedly must not leak one file per press.
 */
export function downloadCsv(filename: string, body: Blob | string): void {
  const blob = typeof body === 'string' ? new Blob([body], { type: 'text/csv;charset=utf-8' }) : body;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
